import { SectionShell } from "@/components/design-system/SectionShell";
import { FONTS } from "@/components/design-system/palette";

const code = `// Set in design-system.css via @theme:
//   --font-display: 'Stack Sans Text', ui-sans-serif, system-ui, sans-serif;
//   --font-sans: 'DM Sans', ui-sans-serif, system-ui, sans-serif;

<h1 className="font-display">Display headline</h1>
<p className="font-sans">Body paragraph.</p>

// Public-marketing variants pair a semantic element with a documented role:
<h1 className="type-marketing-hero-display">Hero headline</h1>
<p className="type-marketing-prose type-marketing-muted">Supporting copy</p>
<strong className="type-marketing-metric">83 hours</strong>`;

const MARKETING_TYPE_VARIANTS = [
  ["type-marketing-hero-display", "One homepage hero heading"],
  ["type-marketing-section-display", "Primary marketing-section headings"],
  ["type-marketing-panel-display", "Large editorial comparison headings"],
  ["type-marketing-statement", "A single high-impact position statement"],
  ["type-marketing-list-display", "Titles in numbered service and process lists"],
  ["type-marketing-lead", "Hero supporting copy"],
  ["type-marketing-prose", "Long-form marketing body copy"],
  ["type-marketing-body-small", "Compact supporting paragraphs and links"],
  ["type-marketing-detail", "Dense labels inside diagrams and comparisons"],
  ["type-marketing-caption", "Diagram and figure captions"],
  ["type-marketing-node-label", "Small labels inside system diagrams"],
  ["type-marketing-node-title", "Primary system-diagram node"],
  ["type-marketing-node", "Secondary system-diagram nodes and founder labels"],
  ["type-marketing-index", "Sequence and diagram indices"],
  ["type-marketing-metric", "Audited outcome values"],
  ["type-marketing-microcopy", "Sources, dates, and supporting notes"],
  ["type-marketing-code", "Compact process artifacts"],
  ["type-marketing-inverse", "Text on dark marketing surfaces"],
  ["type-marketing-inverse-muted", "Secondary text on dark marketing surfaces"],
  ["type-marketing-muted", "Secondary text on light marketing surfaces"],
  ["type-marketing-body-color", "Body emphasis on tinted surfaces"],
  ["type-marketing-ink", "Display-color text on light surfaces"],
  ["type-marketing-accent-display", "Accent-colored marketing labels"],
  ["type-marketing-signal-display", "Signal-colored evidence and diagram labels"],
] as const;

export function TypographySection() {
  return (
    <SectionShell
      id="typography"
      title="Typography"
      description={
        <>
          Two families. Headlines use <code>{FONTS.display}</code>, body copy
          uses <code>{FONTS.body}</code>. Both load from Google Fonts via{" "}
          <code>&lt;link&gt;</code> tags in the app's HTML <code>&lt;head&gt;</code>.
        </>
      }
      whenToUse={
        <ul>
          <li>
            Use <code>font-display</code> for headings only. Headings already
            inherit it via base styles.
          </li>
          <li>
            Use <code>font-sans</code> for everything else — also already the
            default.
          </li>
          <li>
            On public marketing pages, use the documented{" "}
            <code>type-marketing-*</code> role that matches the content, then
            add a contextual color variant only when the surface requires it.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            Don't import additional font families — propose adding to the
            system instead.
          </li>
          <li>Don't override <code>font-family</code> inline.</li>
        </ul>
      }
      preview={
        <div className="space-y-6">
          <div>
            <div className="mb-1 text-xs font-mono text-ink-muted">
              Display — {FONTS.display}
            </div>
            <div className="font-display text-4xl font-semibold text-ink-display">
              The quick brown fox jumps over the lazy dog
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-mono text-ink-muted">
              Body — {FONTS.body}
            </div>
            <div className="font-sans text-base text-ink-body">
              The quick brown fox jumps over the lazy dog. 0123456789. The
              five boxing wizards jump quickly.
            </div>
          </div>
          <div className="space-y-3 border-t border-hairline pt-6">
            <div className="text-xs font-mono text-ink-muted">
              Public marketing roles
            </div>
            {MARKETING_TYPE_VARIANTS.map(([className, usage]) => (
              <div
                className="grid gap-2 rounded-md border border-hairline p-3 md:grid-cols-[minmax(220px,0.8fr)_1fr]"
                key={className}
              >
                <code>{className}</code>
                <div
                  className={`${className} ${className.includes("inverse") ? "bg-ink-display p-2" : ""}`}
                >
                  {usage}
                </div>
              </div>
            ))}
          </div>
        </div>
      }
      code={code}
    />
  );
}
